import type { FC } from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, TriangleAlert } from "lucide-react";

import type { ContractContactGroup } from "../types";

import { useContractContactsService } from "../services/use-contract-contacts-service";
import { contractContactGroups } from "../utils/contract-contact-row";

import DeleteOccupantDialog from "./delete-occupant-dialog";
import OccupantDrawer from "./occupant-drawer";
import OccupantsTable from "./occupants-table";
import OccupantsTableSkeleton from "./occupants-table-skeleton";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

/** How many automatic alerts a contract may hold, spread across its contacts.
 * Hardcoded because nothing serves it yet — the alerts step has carried the
 * same FIXME since the count was first shown. One row is one alert. */
const MAX_AUTOMATIC_ALERTS = 3;

interface ContactsCardProps {
  contractId: string | number;
  /** Advances the flow — the card's own footer owns the Confirm button. */
  onConfirm: () => void;
}

/**
 * The contract's saved contacts: who gets alerted, at which number or address,
 * on which channels. One card — header, table, confirm — as the alerts step
 * renders it.
 */
const ContactsCard: FC<ContactsCardProps> = ({ contractId, onConfirm }) => {
  const { t } = useTranslation(["occupants"]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Which contact the drawer is editing; `undefined` opens it on a blank form.
  const [editingContactId, setEditingContactId] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState<ContractContactGroup | null>(null);

  const contacts = useContractContactsService(contractId);
  const groups = contractContactGroups(contacts.data?.data ?? []);
  // One subscription row is one automatic alert, whichever contact holds it.
  const alertCount = contacts.data?.data.length ?? 0;
  const limitReached = alertCount >= MAX_AUTOMATIC_ALERTS;

  const openDrawer = (contactId?: string) => {
    setEditingContactId(contactId);
    setDrawerOpen(true);
  };

  return (
    <div className="follo-card mt-7">
      <div className="flex items-start justify-between gap-4 px-8 py-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="follo-label">{t("occupants:occupants-card-title", "Saved contacts")}</h3>

          <p className="flex flex-wrap items-center gap-x-1 text-sm font-medium text-muted-foreground">
            {t("occupants:occupants-card-subtitle", "Limited to {{max}} automatic alerts shared between contacts.", {
              max: MAX_AUTOMATIC_ALERTS,
            })}
            {limitReached && (
              <span className="flex items-center gap-1 text-[#c4841d]">
                {t("occupants:occupants-limit-reached", "({{count}}/{{max}} limit reached)", {
                  count: alertCount,
                  max: MAX_AUTOMATIC_ALERTS,
                })}
                <TriangleAlert className="size-3.5 shrink-0" />
              </span>
            )}
          </p>
        </div>

        <Button
          className="shrink-0 cursor-pointer bg-white shadow-xs"
          type="button"
          variant="outline"
          onClick={() => openDrawer()}
        >
          <Plus />
          {t("occupants:occupants-add-contact", "Add a contact")}
        </Button>
      </div>

      <Separator />

      {contacts.isPending ? (
        <OccupantsTableSkeleton />
      ) : groups.length === 0 ? (
        <p className="px-8 py-10 text-center text-sm text-muted-foreground">
          {t("occupants:occupants-empty", "No contact has been added to this contract yet")}
        </p>
      ) : (
        <OccupantsTable
          contractId={contractId}
          groups={groups}
          limitReached={limitReached}
          onDelete={setDeleting}
          onEdit={openDrawer}
        />
      )}

      <div className="flex justify-end px-8 py-4">
        <Button className="cursor-pointer" type="button" onClick={onConfirm}>
          {t("occupants:occupants-confirm", "Confirm")}
        </Button>
      </div>

      <OccupantDrawer
        contactId={editingContactId}
        contractId={contractId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <DeleteOccupantDialog
        contractId={contractId}
        group={deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      />
    </div>
  );
};

export default ContactsCard;
